import React, { useState, useRef } from 'react'
import {Upload, X, FileText, AlertTriangle, CheckCircle, Download} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface BulkUploadComposicoesProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  insumos: any[]
  composicoes: any[]
}

interface ComposicaoItem {
  nome: string
  tipo: 'material' | 'mao_obra'
  valorUnitario: number
  quantidade: number
  subtotal: number
}

interface ComposicaoRow {
  nome?: string
  unidadeMedida?: string
  itens?: ComposicaoItem[]
  valorTotal?: number
  observacoes?: string
}

interface ValidationError {
  row: number
  field: string
  message: string
}

const BulkUploadComposicoes: React.FC<BulkUploadComposicoesProps> = ({
  isOpen,
  onClose,
  onSuccess,
  insumos,
  composicoes
}) => {
  const [dragActive, setDragActive] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [parsedData, setParsedData] = useState<ComposicaoRow[]>([])
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [uploadStep, setUploadStep] = useState<'upload' | 'preview' | 'processing' | 'complete'>('upload')
  const [processedCount, setProcessedCount] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const resetState = () => {
    setUploadedFile(null)
    setParsedData([])
    setValidationErrors([])
    setIsProcessing(false)
    setUploadStep('upload')
    setProcessedCount(0)
    setDragActive(false)
  }

  const handleClose = () => {
    if (!isProcessing) {
      resetState()
      onClose()
    }
  }

  // Gerar template Excel para composições com itens detalhados
  const downloadTemplate = () => {
    try {
      const templateData = [
        // Cabeçalho
        ['composicao_nome', 'composicao_unidade', 'item_nome', 'item_tipo', 'item_valor_unitario', 'item_quantidade', 'item_subtotal', 'composicao_observacoes'],
        // Dados de exemplo - Composição 1: Concreto FCK 25
        ['Concreto FCK 25', 'm³', 'Cimento CP II-E-32', 'material', '0.65', '350', '227.50', 'Concreto estrutural'],
        ['Concreto FCK 25', 'm³', 'Areia média', 'material', '0.08', '600', '48.00', ''],
        ['Concreto FCK 25', 'm³', 'Brita 1', 'material', '0.12', '800', '96.00', ''],
        ['Concreto FCK 25', 'm³', 'Pedreiro', 'mao_obra', '25.00', '2', '50.00', ''],
        ['Concreto FCK 25', 'm³', 'Servente', 'mao_obra', '18.00', '4', '72.00', ''],
        // Composição 2: Alvenaria
        ['Alvenaria Tijolo Cerâmico', 'm²', 'Tijolo cerâmico 6 furos', 'material', '0.45', '85', '38.25', 'Alvenaria de vedação'],
        ['Alvenaria Tijolo Cerâmico', 'm²', 'Argamassa traço 1:3', 'material', '0.35', '25', '8.75', ''],
        ['Alvenaria Tijolo Cerâmico', 'm²', 'Pedreiro', 'mao_obra', '25.00', '0.8', '20.00', ''],
        ['Alvenaria Tijolo Cerâmico', 'm²', 'Servente', 'mao_obra', '18.00', '0.4', '7.20', ''],
        // Composição 3: Reboco
        ['Reboco Interno', 'm²', 'Argamassa traço 1:4', 'material', '0.32', '20', '6.40', 'Reboco interno espessura 2cm'],
        ['Reboco Interno', 'm²', 'Pedreiro', 'mao_obra', '25.00', '0.5', '12.50', ''],
        ['Reboco Interno', 'm²', 'Servente', 'mao_obra', '18.00', '0.3', '5.40', '']
      ]

      // Criar conteúdo CSV com separador de vírgula e encoding UTF-8
      const csvContent = templateData.map(row => 
        row.map(cell => {
          const cellStr = String(cell)
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`
          }
          return cellStr
        }).join(',')
      ).join('\n')

      // Adicionar BOM UTF-8 para compatibilidade com Excel
      const BOM = '\uFEFF'
      const finalContent = BOM + csvContent

      // Criar blob com tipo Excel
      const blob = new Blob([finalContent], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      
      // Download do arquivo
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', 'template_composicoes_detalhado.xlsx')
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      toast.success('Template Excel (.xlsx) baixado com sucesso!')
      
    } catch (error) {
      console.error('Erro ao gerar template:', error)
      toast.error('Erro ao gerar template Excel')
    }
  }

  // 🔥 FUNÇÃO AUXILIAR: Converter para número SEM validações restritivas
  const parseNumber = (value: any): number => {
    if (value === null || value === undefined) return 0
    
    // Converter para string e limpar
    let cleanValue = String(value).trim()
    
    // Se estiver vazio, retornar 0
    if (cleanValue === '') return 0
    
    // Substituir vírgula por ponto (formato brasileiro)
    cleanValue = cleanValue.replace(',', '.')
    
    // Remover espaços extras
    cleanValue = cleanValue.replace(/\s+/g, '')
    
    // Tentar converter diretamente
    const num = parseFloat(cleanValue)
    
    // Se não conseguir converter, retornar 0
    return isNaN(num) ? 0 : num
  }

  // 🔥 CORREÇÃO: Validação mais flexível
  const validateRow = (row: any, index: number): ValidationError[] => {
    const errors: ValidationError[] = []
    const rowNum = index + 2 // +2 porque começamos da linha 2 (após header)

    // Nome da composição é obrigatório
    if (!row.composicao_nome || String(row.composicao_nome).trim() === '') {
      errors.push({ row: rowNum, field: 'composicao_nome', message: 'Nome da composição é obrigatório' })
    }

    // Unidade de medida é obrigatória
    if (!row.composicao_unidade || String(row.composicao_unidade).trim() === '') {
      errors.push({ row: rowNum, field: 'composicao_unidade', message: 'Unidade de medida é obrigatória' })
    }

    // Nome do item é obrigatório
    if (!row.item_nome || String(row.item_nome).trim() === '') {
      errors.push({ row: rowNum, field: 'item_nome', message: 'Nome do item é obrigatório' })
    }

    // Tipo do item deve ser 'material' ou 'mao_obra'
    const tipoItem = String(row.item_tipo || '').toLowerCase().trim()
    if (!tipoItem || !['material', 'mao_obra'].includes(tipoItem)) {
      errors.push({ row: rowNum, field: 'item_tipo', message: 'Tipo do item deve ser "material" ou "mao_obra"' })
    }

    return errors
  }

  // 🔥 CORREÇÃO: Parse mais robusto com múltiplos separadores
  const parseTextFile = (text: string): any[] => {
    try {
      const lines = text.split(/\r?\n/).filter(line => line.trim() !== '')
      if (lines.length < 2) return []

      // 🔥 MELHORIA: Detectar separador de forma mais inteligente
      const firstLine = lines[0]
      let separator = ','
      
      // Contar ocorrências de cada separador
      const separatorCounts = {
        ',': (firstLine.match(/,/g) || []).length,
        ';': (firstLine.match(/;/g) || []).length,
        '\t': (firstLine.match(/\t/g) || []).length
      }
      
      // Escolher o separador com mais ocorrências
      if (separatorCounts['\t'] > 0 && separatorCounts['\t'] >= separatorCounts[','] && separatorCounts['\t'] >= separatorCounts[';']) {
        separator = '\t'
      } else if (separatorCounts[';'] > separatorCounts[',']) {
        separator = ';'
      }
      
      console.log('Separador detectado:', separator === '\t' ? 'TAB' : separator)
      
      // 🔥 MELHORIA: Parse mais robusto de CSV com aspas
      const parseCSVLine = (line: string, sep: string): string[] => {
        const result = []
        let current = ''
        let inQuotes = false
        let i = 0
        
        while (i < line.length) {
          const char = line[i]
          
          if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
              // Aspas duplas escapadas
              current += '"'
              i += 2
            } else {
              // Alternar estado das aspas
              inQuotes = !inQuotes
              i++
            }
          } else if (char === sep && !inQuotes) {
            // Separador encontrado fora das aspas
            result.push(current.trim())
            current = ''
            i++
          } else {
            current += char
            i++
          }
        }
        
        result.push(current.trim())
        return result
      }
      
      const headers = parseCSVLine(firstLine, separator).map(h => h.replace(/"/g, '').trim())
      console.log('Headers detectados:', headers)
      
      const rows = []
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i], separator).map(v => v.replace(/"/g, '').trim())
        const row: any = {}
        
        headers.forEach((header, index) => {
          row[header] = values[index] || ''
        })
        
        // Só adicionar se tem nome da composição
        if (row.composicao_nome && row.composicao_nome.trim() !== '') {
          rows.push(row)
        }
      }

      console.log('Linhas parseadas:', rows.length)
      return rows
      
    } catch (error) {
      console.error('Erro no parseTextFile:', error)
      throw new Error('Erro ao processar arquivo texto')
    }
  }

  // Verificar se é arquivo Excel
  const isExcelFile = (file: File): boolean => {
    const name = file.name.toLowerCase()
    const type = file.type.toLowerCase()
    
    return name.endsWith('.xlsx') || 
           name.endsWith('.xls') || 
           type.includes('spreadsheet') ||
           type.includes('excel') ||
           type.includes('ms-excel') ||
           type.includes('officedocument')
  }

  // 🔥 CORREÇÃO: Parse Excel mais robusto
  const parseExcelFile = async (file: File): Promise<any[]> => {
    try {
      const arrayBuffer = await file.arrayBuffer()
      
      // Tentar múltiplas estratégias de decodificação
      const decodingStrategies = [
        () => new TextDecoder('utf-8').decode(arrayBuffer),
        () => new TextDecoder('utf-8', { ignoreBOM: false }).decode(arrayBuffer),
        () => new TextDecoder('iso-8859-1').decode(arrayBuffer),
        () => new TextDecoder('windows-1252').decode(arrayBuffer)
      ]
      
      for (const strategy of decodingStrategies) {
        try {
          const text = strategy()
          console.log('Tentativa de decodificação, primeiros 200 chars:', text.substring(0, 200))
          
          // Verificar se contém dados estruturados
          if (text.includes('composicao_nome') || 
              (text.includes('\t') && text.split('\t').length > 5) || 
              (text.includes(',') && text.split(',').length > 5)) {
            
            const parsed = parseTextFile(text)
            if (parsed.length > 0) {
              console.log('Decodificação bem-sucedida com estratégia')
              return parsed
            }
          }
        } catch (err) {
          console.log('Estratégia de decodificação falhou:', err)
          continue
        }
      }

      throw new Error('Não foi possível decodificar o arquivo Excel')
      
    } catch (error) {
      console.error('Erro ao processar Excel:', error)
      throw new Error('Arquivo Excel não suportado. Salve como CSV UTF-8 ou use o template fornecido.')
    }
  }

  // 🔥 CORREÇÃO PRINCIPAL: Preservar corretamente o nome do item da coluna item_nome
  const groupItemsByComposicao = (rawData: any[]): ComposicaoRow[] => {
    const composicoesMap = new Map<string, ComposicaoRow>()

    rawData.forEach((row, index) => {
      try {
        const nomeComposicao = String(row.composicao_nome || '').trim()
        if (!nomeComposicao) return

        if (!composicoesMap.has(nomeComposicao)) {
          composicoesMap.set(nomeComposicao, {
            nome: nomeComposicao,
            unidadeMedida: String(row.composicao_unidade || 'un').trim(),
            observacoes: String(row.composicao_observacoes || '').trim(),
            itens: [],
            valorTotal: 0
          })
        }

        const composicao = composicoesMap.get(nomeComposicao)!
        
        // 🔥 CORREÇÃO: Garantir que o nome do item vem da coluna item_nome
        if (row.item_nome && row.item_nome.trim() !== '') {
          const item: ComposicaoItem = {
            nome: String(row.item_nome).trim(), // 🔥 GARANTIR que usa o valor da coluna item_nome
            tipo: String(row.item_tipo || 'material').toLowerCase() as 'material' | 'mao_obra',
            valorUnitario: parseNumber(row.item_valor_unitario),
            quantidade: parseNumber(row.item_quantidade),
            subtotal: parseNumber(row.item_subtotal)
          }

          console.log(`Item ${index + 1} - Nome do item:`, {
            nomeOriginal: row.item_nome,
            nomeProcessado: item.nome,
            tipo: item.tipo,
            valorUnitario: item.valorUnitario,
            quantidade: item.quantidade,
            subtotal: item.subtotal
          })

          composicao.itens!.push(item)
        }
      } catch (error) {
        console.error(`Erro ao processar linha ${index + 1}:`, error, row)
      }
    })

    // Calcular valor total somando subtotais
    const result = Array.from(composicoesMap.values())
    result.forEach(composicao => {
      composicao.valorTotal = composicao.itens!.reduce((total, item) => total + item.subtotal, 0)
      console.log(`Composição "${composicao.nome}" - Total: R$ ${composicao.valorTotal.toFixed(2)}`)
      console.log('Itens da composição:', composicao.itens!.map(i => ({ nome: i.nome, tipo: i.tipo })))
    })

    return result
  }

  // 🆕 NOVA FUNCIONALIDADE: Consultar ou criar insumo automaticamente
  const findOrCreateInsumo = async (nomeItem: string, tipoItem: 'material' | 'mao_obra', valorUnitario: number): Promise<string> => {
    try {
      console.log(`🔍 Procurando insumo: "${nomeItem}" (tipo: ${tipoItem})`)
      
      // 1. Consultar insumo existente pelo nome (busca case-insensitive)
      const nomeNormalizado = nomeItem.toLowerCase().trim()
      const insumoExistente = insumos.find(insumo => 
        insumo?.nome?.toLowerCase().trim() === nomeNormalizado
      )

      if (insumoExistente) {
        console.log(`✅ Insumo encontrado: ${insumoExistente.nome} (ID: ${insumoExistente._id})`)
        return insumoExistente._id
      }

      // 2. Se não encontrou, criar novo insumo
      console.log(`📦 Criando novo insumo: "${nomeItem}"`)
      
      const novoInsumo = {
        nome: nomeItem.trim(),
        tipo: tipoItem === 'mao_obra' ? 'mao_obra' : 'material',
        unidadeMedida: tipoItem === 'mao_obra' ? 'h' : 'un', // Default: horas para mão de obra, unidade para material
        precoUnitario: valorUnitario || 0,
        controlaEstoque: tipoItem !== 'mao_obra', // Mão de obra geralmente não controla estoque
        quantidadeEstoque: 0,
        estoqueMinimo: 0,
        ativo: true,
        observacoes: `Criado automaticamente via importação de composições em ${new Date().toLocaleDateString()}`,
        creator: 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      const insumoResult = await lumi.entities.insumos.create(novoInsumo)
      const novoId = insumoResult._id || insumoResult.id

      console.log(`✅ Novo insumo criado: "${nomeItem}" (ID: ${novoId})`)
      
      // Atualizar lista local de insumos para próximas consultas
      insumos.push({
        _id: novoId,
        ...novoInsumo
      })

      return novoId
      
    } catch (error) {
      console.error(`❌ Erro ao processar insumo "${nomeItem}":`, error)
      throw new Error(`Erro ao processar insumo "${nomeItem}": ${error instanceof Error ? error.message : 'Erro desconhecido'}`)
    }
  }

  // Processar arquivo
  const processFile = async (file: File) => {
    setIsProcessing(true)
    
    try {
      console.log('Iniciando processamento do arquivo:', file.name, 'Tipo:', file.type)
      
      let rawData: any[] = []

      if (isExcelFile(file)) {
        console.log('Processando como arquivo Excel...')
        rawData = await parseExcelFile(file)
      } else {
        console.log('Processando como arquivo CSV/TSV...')
        const arrayBuffer = await file.arrayBuffer()
        const text = new TextDecoder('utf-8').decode(arrayBuffer)
        rawData = parseTextFile(text)
      }
      
      console.log('Dados brutos extraídos:', rawData.length, 'linhas')
      console.log('Primeiras 3 linhas:', rawData.slice(0, 3))
      
      if (rawData.length === 0) {
        toast.error('Arquivo vazio ou formato não reconhecido. Use o template fornecido.')
        setIsProcessing(false)
        return
      }

      // Validar dados
      const errors: ValidationError[] = []
      rawData.forEach((row, index) => {
        const rowErrors = validateRow(row, index)
        errors.push(...rowErrors)
      })

      // Agrupar itens por composição
      const groupedData = groupItemsByComposicao(rawData)
      
      // Filtrar composições que já existem
      const existingNames = composicoes.map(c => c.nome.toLowerCase().trim())
      const newComposicoes = groupedData.filter(comp => 
        !existingNames.includes(comp.nome!.toLowerCase().trim())
      )

      console.log('Composições processadas:', newComposicoes.length)
      console.log('Composições filtradas (novas):', newComposicoes)
      
      setValidationErrors(errors)
      setParsedData(newComposicoes)
      setUploadStep('preview')
      
    } catch (error) {
      console.error('Erro ao processar arquivo:', error)
      toast.error(`Erro ao processar arquivo: ${error instanceof Error ? error.message : 'Formato não suportado'}`)
    } finally {
      setIsProcessing(false)
    }
  }

  // 🔥 CORREÇÃO CRÍTICA: Implementar vinculação automática de insumos
  const processValidData = async () => {
    if (parsedData.length === 0) {
      toast.error('Nenhum dado válido para processar')
      return
    }

    setUploadStep('processing')
    setIsProcessing(true)
    setProcessedCount(0)

    try {
      let successCount = 0
      let errorCount = 0

      for (let i = 0; i < parsedData.length; i++) {
        try {
          console.log(`\n🔄 Processando composição ${i + 1}/${parsedData.length}: "${parsedData[i].nome}"`)
          
          // 🆕 ETAPA 1: Processar todos os itens e vincular/criar insumos
          const itensProcessados = []
          
          for (let j = 0; j < parsedData[i].itens!.length; j++) {
            const item = parsedData[i].itens![j]
            
            try {
              console.log(`  📋 Processando item ${j + 1}: "${item.nome}" (${item.tipo})`)
              
              // Consultar ou criar insumo
              const insumoId = await findOrCreateInsumo(item.nome, item.tipo, item.valorUnitario)
              
              // Criar item da composição com ID do insumo vinculado
              const itemProcessado = {
                itemId: insumoId, // 🔥 ID DO INSUMO VINCULADO
                nome: item.nome, // 🔥 MANTER O NOME PARA EXIBIÇÃO
                tipo: 'insumo' as const, // Sempre 'insumo' pois agora está vinculado
                quantidade: item.quantidade,
                precoUnitario: item.valorUnitario,
                subtotal: item.subtotal
              }
              
              itensProcessados.push(itemProcessado)
              console.log(`  ✅ Item processado: "${item.nome}" → ID: ${insumoId}`)
              
            } catch (itemError) {
              console.error(`  ❌ Erro ao processar item "${item.nome}":`, itemError)
              throw new Error(`Erro no item "${item.nome}": ${itemError instanceof Error ? itemError.message : 'Erro desconhecido'}`)
            }
          }

          // 🆕 ETAPA 2: Criar composição com itens vinculados
          const composicaoData = {
            nome: parsedData[i].nome,
            unidadeMedida: parsedData[i].unidadeMedida,
            itens: itensProcessados, // 🔥 ITENS COM IDs DE INSUMOS VINCULADOS
            valorTotal: parsedData[i].valorTotal || 0,
            observacoes: parsedData[i].observacoes || 'Criada via upload em massa com vinculação automática de insumos',
            ativo: true,
            creator: 'admin',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }

          console.log(`🔥 CRIANDO COMPOSIÇÃO - Dados finais:`, {
            nome: composicaoData.nome,
            totalItens: composicaoData.itens.length,
            itens: composicaoData.itens.map(i => ({ 
              nome: i.nome, 
              itemId: i.itemId,
              tipo: i.tipo,
              quantidade: i.quantidade,
              precoUnitario: i.precoUnitario,
              subtotal: i.subtotal
            }))
          })
          
          await lumi.entities.composicoes.create(composicaoData)
          successCount++
          
          console.log(`✅ Composição "${composicaoData.nome}" criada com sucesso (${itensProcessados.length} itens vinculados)`)
          
        } catch (error) {
          console.error(`❌ Erro ao criar composição ${parsedData[i].nome}:`, error)
          errorCount++
        }
        
        setProcessedCount(i + 1)
        await new Promise(resolve => setTimeout(resolve, 100))
      }

      if (successCount > 0) {
        toast.success(`${successCount} composições importadas com sucesso! Todos os itens foram vinculados aos insumos.`)
        if (errorCount > 0) {
          toast.error(`${errorCount} composições falharam na importação`)
        }
        setUploadStep('complete')
        setTimeout(() => {
          onSuccess()
          resetState()
          onClose()
        }, 2000)
      } else {
        toast.error('Nenhuma composição foi importada')
        setUploadStep('preview')
      }

    } catch (error) {
      console.error('Erro no processamento em massa:', error)
      toast.error('Erro no processamento dos dados')
      setUploadStep('preview')
    } finally {
      setIsProcessing(false)
    }
  }

  // Handle file upload
  const handleFileUpload = async (files: FileList) => {
    const file = files[0]
    if (!file) return

    const fileName = file.name.toLowerCase()
    const fileType = file.type.toLowerCase()
    
    const isCSV = fileName.endsWith('.csv') || fileType.includes('csv')
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls') || 
                   fileType.includes('spreadsheet') || fileType.includes('excel') || 
                   fileType.includes('ms-excel') || fileType.includes('officedocument')
    
    if (!isCSV && !isExcel) {
      toast.error('Apenas arquivos CSV, XLS ou XLSX são aceitos')
      return
    }

    console.log('Arquivo aceito:', file.name, 'Tipo:', file.type)
    setUploadedFile(file)
    await processFile(file)
  }

  // Drag and drop handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Upload className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Upload em Massa - Composições com Vinculação Automática</h2>
          </div>
          {!isProcessing && (
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {uploadStep === 'upload' && (
            <div className="space-y-6">
              {/* Template Download */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-blue-900 mb-2">Template Excel (.xlsx) - Composições com Vinculação Automática</h3>
                    <p className="text-blue-700 text-sm mb-3">
                      Baixe o template com as colunas: <strong>composicao_nome, composicao_unidade, item_nome, item_tipo (material/mao_obra), item_valor_unitario, item_quantidade, item_subtotal, composicao_observacoes</strong>.
                      <br />
                      <em>🔗 NOVO: Os itens serão automaticamente vinculados aos insumos existentes ou novos insumos serão criados!</em>
                    </p>
                    <button
                      onClick={downloadTemplate}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2 text-sm transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Baixar Template Excel (.xlsx)
                    </button>
                  </div>
                </div>
              </div>

              {/* Upload Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Arraste seu arquivo aqui ou clique para selecionar
                </h3>
                <p className="text-gray-500 mb-4">
                  Formatos aceitos: CSV, XLS, XLSX (UTF-8)
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Selecionar Arquivo
                </button>
              </div>

              {isProcessing && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-gray-600">Processando arquivo...</p>
                </div>
              )}
            </div>
          )}

          {uploadStep === 'preview' && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">Resumo do Arquivo</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Arquivo:</span>
                    <span className="ml-2 font-medium">{uploadedFile?.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Composições válidas:</span>
                    <span className="ml-2 font-medium text-green-600">{parsedData.length}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Erros encontrados:</span>
                    <span className="ml-2 font-medium text-red-600">{validationErrors.length}</span>
                  </div>
                </div>
                <div className="mt-3 text-sm text-gray-600">
                  <em>🔗 Total de itens que serão vinculados/criados: {parsedData.reduce((sum, comp) => sum + (comp.itens?.length || 0), 0)}</em>
                </div>
              </div>

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-medium text-red-900 mb-2">Erros de Validação</h3>
                      <div className="max-h-40 overflow-y-auto">
                        {validationErrors.slice(0, 10).map((error, index) => (
                          <div key={index} className="text-sm text-red-700 mb-1">
                            Linha {error.row}, campo "{error.field}": {error.message}
                          </div>
                        ))}
                        {validationErrors.length > 10 && (
                          <div className="text-sm text-red-600 mt-2">
                            ... e mais {validationErrors.length - 10} erros
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview Data */}
              {parsedData.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">
                    Preview das Composições Válidas ({parsedData.length} composições)
                  </h3>
                  <div className="space-y-4">
                    {parsedData.slice(0, 3).map((composicao, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900">{composicao.nome}</h4>
                            <p className="text-sm text-gray-600">
                              {composicao.unidadeMedida} • {composicao.itens?.length || 0} itens
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-green-600">
                              R$ {(composicao.valorTotal || 0).toFixed(2)}
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          {composicao.itens?.slice(0, 3).map((item, itemIndex) => (
                            <div key={itemIndex} className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded">
                              <div className="flex items-center gap-2">
                                <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                                  {item.tipo === 'mao_obra' ? '👷 M.O.' : '📦 Mat.'}
                                </span>
                                <span className="font-medium">{item.nome}</span>
                                <span className="text-xs text-green-600">🔗 Auto-vinculado</span>
                              </div>
                              <div className="text-gray-600">
                                {item.quantidade} × R$ {item.valorUnitario.toFixed(2)} = R$ {item.subtotal.toFixed(2)}
                              </div>
                            </div>
                          ))}
                          {(composicao.itens?.length || 0) > 3 && (
                            <div className="text-xs text-gray-500 text-center">
                              ... e mais {(composicao.itens?.length || 0) - 3} itens
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {parsedData.length > 3 && (
                      <div className="text-center text-gray-500 text-sm">
                        ... e mais {parsedData.length - 3} composições
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 justify-between">
                <button
                  onClick={() => setUploadStep('upload')}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Voltar
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  {parsedData.length > 0 && (
                    <button
                      onClick={processValidData}
                      disabled={isProcessing}
                      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {isProcessing ? 'Processando...' : `Importar ${parsedData.length} Composições`}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {uploadStep === 'processing' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Importando Composições</h3>
              <p className="text-gray-600 mb-4">
                Processando {processedCount} de {parsedData.length} composições...
                <br />
                <small className="text-blue-600">🔗 Vinculando itens aos insumos automaticamente</small>
              </p>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${(processedCount / parsedData.length) * 100}%` }}
                ></div>
              </div>
              <div className="mt-2 text-sm text-gray-500">
                {Math.round((processedCount / parsedData.length) * 100)}% concluído
              </div>
            </div>
          )}

          {uploadStep === 'complete' && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Importação Concluída!</h3>
              <p className="text-gray-600">
                {parsedData.length} composições foram importadas com sucesso.
                <br />
                <small className="text-green-600">🔗 Todos os itens foram vinculados aos insumos automaticamente!</small>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BulkUploadComposicoes